/**
 * AdminLeadership — governance console for the Church Workspace.
 *
 * Route: /admin/leadership (super-admin only)
 *
 * Layout:
 *   - Top: summary tiles (positions, active, acting, vacant HQ, delegations)
 *   - Level tabs (Global HQ · Province · Region · District · Assembly · Department · Home Group)
 *   - Two columns per tab: Positions | Appointments for the selected position
 *   - Actions: create custom position, appoint person, end appointment, delegate
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft, Crown, UserPlus, Users, AlertCircle, Plus, X,
  ArrowUpRight, Clock, Shield, HandCoins, ScrollText,
  MessageSquare, Send, GitBranch, LayoutGrid, Network, Building2,
  AlertTriangle, Printer, FileText,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';
import OrgChartTree from '@/components/OrgChartTree';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const LEVEL_LABELS = {
  global_hq: 'Global HQ',
  province: 'Province',
  region: 'Region',
  district: 'District',
  campus: 'Campus',
  assembly: 'Branch',
  department: 'Department',
  home_group: 'Home Group',
};

const STATUS_TONE = {
  active: 'border-emerald-500/40 text-emerald-300',
  acting: 'border-amber-500/40 text-amber-300',
  suspended: 'border-slate-600 text-slate-400',
  retired: 'border-slate-700 text-slate-500',
  archived: 'border-slate-800 text-slate-500 line-through',
};

const Stat = ({ label, value, tone = 'slate', icon: Icon, testid }) => {
  const tones = {
    slate: 'from-slate-500/10 to-slate-500/5 text-slate-300 border-slate-500/30',
    violet: 'from-violet-500/10 to-violet-500/5 text-violet-300 border-violet-500/30',
    emerald: 'from-emerald-500/10 to-emerald-500/5 text-emerald-300 border-emerald-500/30',
    amber: 'from-amber-500/10 to-amber-500/5 text-amber-300 border-amber-500/30',
    rose: 'from-rose-500/10 to-rose-500/5 text-rose-300 border-rose-500/30',
  };
  return (
    <div data-testid={testid} className={`rounded-xl border bg-gradient-to-br p-3 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider opacity-80">{label}</span>
        {Icon && <Icon className="h-3.5 w-3.5 opacity-70" />}
      </div>
      <div className="mt-1 text-xl font-semibold">{value ?? '—'}</div>
    </div>
  );
};

export default function AdminLeadership() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [level, setLevel] = useState('global_hq');
  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [createPosOpen, setCreatePosOpen] = useState(false);
  const [appointOpen, setAppointOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');   // 'grid' | 'chart'
  const [delegateFor, setDelegateFor] = useState(null); // appointment obj
  const [messageAllOpen, setMessageAllOpen] = useState(false);
  const [playbookOpen, setPlaybookOpen] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState(false);

  const summaryQ = useQuery({
    queryKey: ['ldr-summary'],
    queryFn: async () => (await axios.get(`${API}/api/admin/leadership/summary`, { headers: authH() })).data,
    refetchInterval: 30_000,
  });

  const positionsQ = useQuery({
    queryKey: ['ldr-positions', level],
    queryFn: async () => (await axios.get(`${API}/api/admin/leadership/positions?level=${level}`, { headers: authH() })).data,
  });

  const appointmentsQ = useQuery({
    queryKey: ['ldr-appointments', selectedPositionId],
    queryFn: async () => (await axios.get(`${API}/api/admin/leadership/appointments?position_id=${selectedPositionId}&include_ended=true`, { headers: authH() })).data,
    enabled: !!selectedPositionId,
  });

  const orgChartQ = useQuery({
    queryKey: ['ldr-orgchart'],
    queryFn: async () => (await axios.get(`${API}/api/admin/leadership/org-chart`, { headers: authH() })).data,
    enabled: viewMode === 'chart',
    refetchInterval: viewMode === 'chart' ? 20_000 : false,
  });

  const delegationsQ = useQuery({
    queryKey: ['ldr-delegations'],
    queryFn: async () => (await axios.get(`${API}/api/admin/leadership/delegations?active_only=true`, { headers: authH() })).data,
    refetchInterval: 30_000,
  });

  const vacancyAlertsQ = useQuery({
    queryKey: ['ldr-vacancy-alerts'],
    queryFn: async () => (await axios.get(`${API}/api/admin/leadership/vacancy-alerts?threshold=3`, { headers: authH() })).data,
    refetchInterval: 60_000,
  });

  const positions = positionsQ.data?.items || [];
  const selectedPos = useMemo(
    () => positions.find((p) => p.id === selectedPositionId),
    [positions, selectedPositionId],
  );

  const endAppointment = useMutation({
    mutationFn: async ({ id, reason }) => {
      const r = await axios.post(
        `${API}/api/admin/leadership/appointments/${id}/end`,
        { reason },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: () => {
      toast.success('Appointment ended');
      qc.invalidateQueries({ queryKey: ['ldr-appointments'] });
      qc.invalidateQueries({ queryKey: ['ldr-summary'] });
      qc.invalidateQueries({ queryKey: ['ldr-orgchart'] });
      qc.invalidateQueries({ queryKey: ['ldr-delegations'] });
    },
    onError: (e) => toast.error(`End failed: ${e?.response?.data?.detail || e.message}`),
  });

  const revokeDelegation = useMutation({
    mutationFn: async (delegationId) => (
      await axios.post(`${API}/api/admin/leadership/delegations/${delegationId}/revoke`, {}, { headers: authH() })
    ).data,
    onSuccess: () => {
      toast.success('Delegation revoked');
      qc.invalidateQueries({ queryKey: ['ldr-delegations'] });
      qc.invalidateQueries({ queryKey: ['ldr-summary'] });
    },
    onError: (e) => toast.error(`Revoke failed: ${e?.response?.data?.detail || e.message}`),
  });

  const summary = summaryQ.data?.totals || {};
  const byLevel = summaryQ.data?.by_level || {};

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-leadership-page">
      <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="mb-1 text-slate-400 hover:text-slate-100"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Crown className="h-6 w-6 text-amber-400" />
              Church Leadership · Governance Console
            </h1>
            <p className="text-xs text-slate-500">
              Positions · Appointments · Delegations · Succession History — every change audited via `leadership_audit`.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1 rounded-full border border-slate-800 bg-slate-900 p-0.5">
              <button
                type="button"
                data-testid="view-mode-grid"
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-amber-500/15 text-amber-200'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <LayoutGrid className="h-3 w-3" /> Grid
              </button>
              <button
                type="button"
                data-testid="view-mode-chart"
                onClick={() => setViewMode('chart')}
                className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs transition-colors ${
                  viewMode === 'chart'
                    ? 'bg-amber-500/15 text-amber-200'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Network className="h-3 w-3" /> Org Chart
              </button>
            </div>
            <Button
              data-testid="create-position-btn"
              variant="outline"
              size="sm"
              className="border-violet-500/40 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20"
              onClick={() => setCreatePosOpen(true)}
            >
              <Plus className="mr-1 h-4 w-4" /> Custom Position
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
          <Stat testid="stat-positions" label="Positions" value={summary.positions} tone="slate" icon={ScrollText} />
          <Stat testid="stat-active" label="Active" value={summary.active_appointments} tone="emerald" icon={Users} />
          <Stat testid="stat-acting" label="Acting" value={summary.acting_appointments} tone="amber" icon={Clock} />
          <Stat testid="stat-holders" label="Holders" value={summary.unique_holders} tone="violet" icon={UserPlus} />
          <Stat testid="stat-vacant-hq" label="Vacant HQ" value={summary.vacant_hq_positions} tone="rose" icon={AlertCircle} />
          <Stat testid="stat-delegations" label="Delegations" value={summary.active_delegations} tone="amber" icon={HandCoins} />
        </div>

        {/* Vacancy Alert banner — pinned when any level has ≥ threshold vacant positions */}
        {!dismissedAlerts && (vacancyAlertsQ.data?.levels_over_threshold || []).length > 0 && (
          <div
            data-testid="vacancy-alert-banner"
            className="rounded-lg border border-rose-500/40 bg-gradient-to-r from-rose-500/10 to-amber-500/5 p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 text-rose-400 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-rose-200">
                    {vacancyAlertsQ.data.levels_over_threshold.length} level{vacancyAlertsQ.data.levels_over_threshold.length === 1 ? '' : 's'}
                    {' '}with {vacancyAlertsQ.data.threshold}+ vacant positions
                    <span className="ml-2 text-xs font-normal text-slate-400">
                      · {vacancyAlertsQ.data.total_vacant} total open
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {vacancyAlertsQ.data.levels_over_threshold.map((lv) => (
                      <button
                        type="button"
                        key={lv.level}
                        data-testid={`vacancy-chip-${lv.level}`}
                        onClick={() => { setViewMode('grid'); setLevel(lv.level); setSelectedPositionId(null); }}
                        className="rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[11px] text-rose-200 hover:bg-rose-500/20"
                        title={
                          lv.sample_vacant?.length
                            ? lv.sample_vacant.map((p) => p.name).slice(0, 5).join(', ') + (lv.sample_vacant.length > 5 ? '…' : '')
                            : ''
                        }
                      >
                        {lv.label} · {lv.vacant} vacant
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <button
                type="button"
                data-testid="vacancy-dismiss"
                onClick={() => setDismissedAlerts(true)}
                className="text-slate-500 hover:text-slate-200"
                title="Dismiss for this session"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Level tabs (only in grid view) */}
        {viewMode === 'grid' && (
        <div className="flex flex-wrap gap-1" data-testid="level-tabs">
          {Object.entries(LEVEL_LABELS).map(([k, label]) => {
            const active = level === k;
            const count = byLevel[k]?.positions || 0;
            return (
              <button
                type="button"
                key={k}
                data-testid={`level-tab-${k}`}
                onClick={() => { setLevel(k); setSelectedPositionId(null); }}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  active
                    ? 'border-amber-500 bg-amber-500/15 text-amber-200'
                    : 'border-slate-800 bg-slate-950 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                }`}
              >
                {label} <span className="ml-1 text-[10px] opacity-60">{count}</span>
              </button>
            );
          })}
        </div>
        )}

        {viewMode === 'chart' ? (
          <Card className="border-slate-800 bg-slate-900/60" data-testid="orgchart-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Network className="h-4 w-4 text-amber-300" />
                Live Org Chart
                <Badge variant="outline" className="ml-2 border-emerald-500/40 text-emerald-300 text-[10px]">
                  auto-refresh 20s
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orgChartQ.isLoading && (
                <p className="text-xs text-slate-500">Loading org chart…</p>
              )}
              {orgChartQ.data?.tree && (
                <OrgChartTree
                  tree={orgChartQ.data.tree}
                  onSelectNode={(node) => {
                    setViewMode('grid');
                    setLevel(node.level);
                    setSelectedPositionId(node.id);
                  }}
                />
              )}
            </CardContent>
          </Card>
        ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Positions column */}
          <Card className="border-slate-800 bg-slate-900/60" data-testid="positions-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Shield className="h-4 w-4 text-violet-300" />
                Positions · {LEVEL_LABELS[level]}
                <Badge variant="outline" className="ml-2 border-slate-700 text-xs text-slate-400">
                  {positions.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto">
              {positionsQ.isLoading ? (
                <p className="text-xs text-slate-500">Loading…</p>
              ) : (
                <div className="space-y-1">
                  {positions.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      data-testid={`position-row-${p.id}`}
                      onClick={() => setSelectedPositionId(p.id)}
                      className={`flex w-full items-center justify-between rounded border px-3 py-2 text-left text-sm transition-colors ${
                        selectedPositionId === p.id
                          ? 'border-amber-500 bg-amber-500/10 text-amber-200'
                          : 'border-slate-800 bg-slate-950 text-slate-300 hover:border-slate-700 hover:bg-slate-900'
                      }`}
                    >
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-[10px] text-slate-500">
                          {p.category}
                          {p.multiple_holders_allowed && ' · multi-holder'}
                          {p.is_custom && ' · custom'}
                        </div>
                      </div>
                      <ArrowUpRight className="h-3 w-3 opacity-40" />
                    </button>
                  ))}
                  {positions.length === 0 && (
                    <p className="p-3 text-xs text-slate-500">No positions for this level.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Appointments column */}
          <Card className="border-slate-800 bg-slate-900/60" data-testid="appointments-card">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4 text-emerald-300" />
                {selectedPos ? selectedPos.name : 'Select a position'}
              </CardTitle>
              {selectedPos && (
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="playbook-btn"
                    variant="outline"
                    size="sm"
                    className="border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                    onClick={() => setPlaybookOpen(true)}
                  >
                    <FileText className="mr-1 h-4 w-4" /> Playbook
                  </Button>
                  <Button
                    data-testid="message-all-btn"
                    variant="outline"
                    size="sm"
                    className="border-sky-500/40 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20"
                    onClick={() => setMessageAllOpen(true)}
                  >
                    <MessageSquare className="mr-1 h-4 w-4" /> Message All
                  </Button>
                  <Button
                    data-testid="appoint-btn"
                    variant="outline"
                    size="sm"
                    className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                    onClick={() => setAppointOpen(true)}
                  >
                    <UserPlus className="mr-1 h-4 w-4" /> Appoint
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto">
              {!selectedPos && (
                <p className="text-xs text-slate-500">
                  Pick a position from the left to see current holders + succession history.
                </p>
              )}
              {selectedPos && appointmentsQ.isLoading && (
                <p className="text-xs text-slate-500">Loading appointments…</p>
              )}
              {selectedPos && (appointmentsQ.data?.items || []).length === 0 && !appointmentsQ.isLoading && (
                <div className="rounded border border-dashed border-slate-700 p-4 text-center text-xs text-slate-500">
                  No appointments yet — this position is <span className="text-rose-300">vacant</span>.
                </div>
              )}
              <div className="space-y-1">
                {(appointmentsQ.data?.items || []).map((a) => (
                  <div
                    key={a.id}
                    data-testid={`appointment-row-${a.id}`}
                    className="flex items-center justify-between rounded border border-slate-800 bg-slate-950 px-3 py-2 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">
                        {a.person_name || 'Unknown person'}
                        {a.is_acting && (
                          <Badge variant="outline" className="ml-2 border-amber-500/40 text-amber-300">acting</Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {a.person_email || '—'}
                        {a.unit_name && ` · ${a.unit_name}`}
                        {a.start_date && ` · from ${a.start_date.slice(0, 10)}`}
                        {a.end_date && ` → ${a.end_date.slice(0, 10)}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={STATUS_TONE[a.status] || STATUS_TONE.slate}>
                        {a.status}
                      </Badge>
                      {['active', 'acting'].includes(a.status) && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-slate-400 hover:text-violet-300"
                            onClick={() => setDelegateFor(a)}
                            data-testid={`delegate-appointment-${a.id}`}
                            title="Delegate authority"
                          >
                            <GitBranch className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-slate-400 hover:text-rose-300"
                            onClick={() => {
                              const reason = window.prompt('End reason (optional)') || '';
                              endAppointment.mutate({ id: a.id, reason });
                            }}
                            data-testid={`end-appointment-${a.id}`}
                            title="End appointment"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Active Delegations panel */}
        <Card className="border-slate-800 bg-slate-900/60" data-testid="delegations-card">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <HandCoins className="h-4 w-4 text-amber-300" />
              Active & Scheduled Delegations
              <Badge variant="outline" className="ml-2 border-slate-700 text-xs text-slate-400">
                {(delegationsQ.data?.items || []).length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {delegationsQ.isLoading && <p className="text-xs text-slate-500">Loading…</p>}
            {(delegationsQ.data?.items || []).length === 0 && !delegationsQ.isLoading && (
              <p className="rounded border border-dashed border-slate-800 p-3 text-center text-[11px] text-slate-500">
                No active delegations. Click the branch icon next to any appointment to delegate its authority.
              </p>
            )}
            <div className="space-y-1">
              {(delegationsQ.data?.items || []).map((d) => (
                <div
                  key={d.id}
                  data-testid={`delegation-row-${d.id}`}
                  className="flex items-center justify-between rounded border border-slate-800 bg-slate-950 px-3 py-2 text-xs"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-200 truncate">
                      <span className="text-slate-400">{d.delegator_name || 'Delegator'}</span>
                      <span className="mx-1 text-slate-600">→</span>
                      <span className="text-emerald-200">{d.delegatee_name || 'Delegatee'}</span>
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-500">
                      {d.scope} · {d.start_date?.slice(0, 10)} → {d.end_date?.slice(0, 10)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={d.status === 'active' ? 'border-emerald-500/40 text-emerald-300' : 'border-amber-500/40 text-amber-300'}>
                      {d.status}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-slate-400 hover:text-rose-300"
                      onClick={() => {
                        if (window.confirm('Revoke this delegation?')) revokeDelegation.mutate(d.id);
                      }}
                      data-testid={`revoke-delegation-${d.id}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-[10px] text-slate-500">
          {summary.default_positions ?? 0} default positions · {summary.custom_positions ?? 0} custom · never delete history
        </p>

        {/* Create custom position dialog */}
        <CreatePositionDialog
          open={createPosOpen}
          onOpenChange={setCreatePosOpen}
          defaultLevel={level}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['ldr-positions'] });
            qc.invalidateQueries({ queryKey: ['ldr-summary'] });
          }}
        />

        {/* Appointment dialog */}
        {selectedPos && (
          <AppointDialog
            open={appointOpen}
            onOpenChange={setAppointOpen}
            position={selectedPos}
            onCreated={() => {
              qc.invalidateQueries({ queryKey: ['ldr-appointments'] });
              qc.invalidateQueries({ queryKey: ['ldr-summary'] });
              qc.invalidateQueries({ queryKey: ['ldr-orgchart'] });
            }}
          />
        )}

        {/* Delegation dialog */}
        {delegateFor && (
          <DelegationDialog
            open={!!delegateFor}
            onOpenChange={(v) => { if (!v) setDelegateFor(null); }}
            appointment={delegateFor}
            onCreated={() => {
              qc.invalidateQueries({ queryKey: ['ldr-delegations'] });
              qc.invalidateQueries({ queryKey: ['ldr-summary'] });
              setDelegateFor(null);
            }}
          />
        )}

        {/* Message-all dialog */}
        {selectedPos && (
          <MessageAllDialog
            open={messageAllOpen}
            onOpenChange={setMessageAllOpen}
            position={selectedPos}
            activeCount={(appointmentsQ.data?.items || []).filter((a) => ['active', 'acting'].includes(a.status)).length}
          />
        )}

        {/* Succession Playbook dialog */}
        {selectedPos && (
          <PlaybookDialog
            open={playbookOpen}
            onOpenChange={setPlaybookOpen}
            position={selectedPos}
          />
        )}
      </div>
    </div>
  );
}

// ── Create Position Dialog ──────────────────────────────────────
function CreatePositionDialog({ open, onOpenChange, defaultLevel, onCreated }) {
  const [name, setName] = useState('');
  const [multiple, setMultiple] = useState(false);
  const [notes, setNotes] = useState('');
  const [category, setCategory] = useState('custom');

  const create = useMutation({
    mutationFn: async () => {
      const r = await axios.post(
        `${API}/api/admin/leadership/positions`,
        { level: defaultLevel, name, multiple_holders_allowed: multiple, category, notes },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: () => {
      toast.success('Position created');
      onCreated?.();
      onOpenChange(false);
      setName(''); setNotes(''); setMultiple(false); setCategory('custom');
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-position-dialog" className="bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle>Create Custom Position · {LEVEL_LABELS[defaultLevel]}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            data-testid="new-position-name"
            placeholder="e.g. Director of Legal Affairs"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-slate-950 border-slate-700"
          />
          <Input
            data-testid="new-position-category"
            placeholder="Category (e.g. legal, research)"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-slate-950 border-slate-700"
          />
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={multiple}
              onChange={(e) => setMultiple(e.target.checked)}
              data-testid="new-position-multiple"
            />
            Allow multiple concurrent holders
          </label>
          <Textarea
            data-testid="new-position-notes"
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="bg-slate-950 border-slate-700"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              data-testid="submit-new-position"
              className="bg-violet-600 hover:bg-violet-500"
              onClick={() => create.mutate()}
              disabled={!name.trim() || create.isPending}
            >
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Appoint Dialog ──────────────────────────────────────────────
function AppointDialog({ open, onOpenChange, position, onCreated }) {
  const [search, setSearch] = useState('');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isActing, setIsActing] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');

  // For Global HQ positions the "unit" is implicitly the HQ church — hide picker
  const unitRequired = position?.level && position.level !== 'global_hq';

  const unitsQ = useQuery({
    queryKey: ['ldr-units', position?.level],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/leadership/units?level=${position.level}`,
      { headers: authH() },
    )).data,
    enabled: open && !!position?.level,
  });

  const personSearch = useQuery({
    queryKey: ['ldr-person-search', search],
    queryFn: async () => {
      if (search.trim().length < 2) return { items: [] };
      const r = await axios.get(
        `${API}/api/search/universal?q=${encodeURIComponent(search)}&limit=15`,
        { headers: authH() },
      );
      const items = (r.data?.items || []).filter((it) => it.kind === 'person');
      return { items };
    },
    enabled: open && search.trim().length >= 2,
  });

  const create = useMutation({
    mutationFn: async () => {
      const r = await axios.post(
        `${API}/api/admin/leadership/appointments`,
        {
          position_id: position.id,
          person_id: selectedPerson.id,
          unit_id: selectedUnit?.id || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          is_acting: isActing,
          notes: notes || undefined,
        },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: () => {
      toast.success(`Appointed ${selectedPerson.label || 'person'}`);
      onCreated?.();
      onOpenChange(false);
      setSearch(''); setSelectedPerson(null); setSelectedUnit(null); setIsActing(false);
      setStartDate(''); setEndDate(''); setNotes('');
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="appoint-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Appoint to · {position.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Person</div>
            {selectedPerson ? (
              <div className="flex items-center justify-between rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2">
                <div>
                  <div className="text-sm text-emerald-200" data-testid="selected-person-label">{selectedPerson.label}</div>
                  <div className="text-[10px] text-slate-500">{selectedPerson.subtitle}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedPerson(null)}>Change</Button>
              </div>
            ) : (
              <>
                <Input
                  data-testid="appoint-search"
                  placeholder="Search Person Registry by name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-slate-950 border-slate-700"
                />
                <div className="mt-1 max-h-40 overflow-y-auto space-y-1">
                  {(personSearch.data?.items || []).map((it) => (
                    <button
                      type="button"
                      key={it.id}
                      data-testid={`appoint-person-${it.id}`}
                      onClick={() => setSelectedPerson(it)}
                      className="w-full rounded border border-slate-800 bg-slate-950 px-3 py-2 text-left text-sm hover:border-emerald-500 hover:bg-emerald-500/5"
                    >
                      <div className="text-slate-200">{it.label}</div>
                      <div className="text-[10px] text-slate-500">{it.subtitle}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={isActing}
              onChange={(e) => setIsActing(e.target.checked)}
              data-testid="appoint-acting"
            />
            Acting appointment (temporary)
          </label>

          {/* Unit picker — scoped to this position's level. Hidden for global_hq */}
          {unitRequired && (
            <div data-testid="appoint-unit-picker">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                <Building2 className="h-3 w-3" /> Organisation Unit
                <span className="ml-1 rounded bg-rose-500/10 px-1 py-0.5 text-[9px] text-rose-300">required</span>
              </div>
              {selectedUnit ? (
                <div className="flex items-center justify-between rounded border border-violet-500/40 bg-violet-500/10 px-3 py-2">
                  <div>
                    <div className="text-sm text-violet-200" data-testid="selected-unit-label">{selectedUnit.name}</div>
                    <div className="text-[10px] text-slate-500">{selectedUnit.level}{selectedUnit.city ? ` · ${selectedUnit.city}` : ''}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedUnit(null)}>Change</Button>
                </div>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-1 rounded border border-slate-800 bg-slate-950 p-1">
                  {unitsQ.isLoading && <p className="p-2 text-[10px] text-slate-500">Loading units…</p>}
                  {(unitsQ.data?.items || []).map((u) => (
                    <button
                      type="button"
                      key={u.id}
                      data-testid={`appoint-unit-${u.id}`}
                      onClick={() => setSelectedUnit(u)}
                      className="w-full rounded border border-slate-800 bg-slate-950 px-3 py-2 text-left text-sm hover:border-violet-500 hover:bg-violet-500/5"
                    >
                      <div className="text-slate-200">{u.name}</div>
                      <div className="text-[10px] text-slate-500">{u.level}{u.city ? ` · ${u.city}` : ''}</div>
                    </button>
                  ))}
                  {!unitsQ.isLoading && (unitsQ.data?.items || []).length === 0 && (
                    <p className="p-2 text-[10px] text-slate-500">
                      No units at level <span className="text-amber-300">{position.level}</span> yet.
                      Create one from the Church Hierarchy page first.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Start date</div>
              <Input
                data-testid="appoint-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-slate-950 border-slate-700"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">End date (opt.)</div>
              <Input
                data-testid="appoint-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-slate-950 border-slate-700"
              />
            </div>
          </div>

          <Textarea
            data-testid="appoint-notes"
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="bg-slate-950 border-slate-700 min-h-[60px]"
          />

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              data-testid="submit-appointment"
              className="bg-emerald-600 hover:bg-emerald-500"
              onClick={() => create.mutate()}
              disabled={!selectedPerson || create.isPending || (unitRequired && !selectedUnit)}
            >
              {create.isPending ? 'Appointing…' : 'Confirm Appointment'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Delegation Dialog ───────────────────────────────────────────
function DelegationDialog({ open, onOpenChange, appointment, onCreated }) {
  const [search, setSearch] = useState('');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [scope, setScope] = useState('approval_authority');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');

  const personSearch = useQuery({
    queryKey: ['ldr-del-person-search', search],
    queryFn: async () => {
      if (search.trim().length < 2) return { items: [] };
      const r = await axios.get(
        `${API}/api/search/universal?q=${encodeURIComponent(search)}&limit=15`,
        { headers: authH() },
      );
      return { items: (r.data?.items || []).filter((it) => it.kind === 'person') };
    },
    enabled: open && search.trim().length >= 2,
  });

  const create = useMutation({
    mutationFn: async () => {
      const r = await axios.post(
        `${API}/api/admin/leadership/appointments/${appointment.id}/delegate`,
        {
          delegatee_person_id: selectedPerson.id,
          scope,
          start_date: startDate || undefined,
          end_date: endDate,
          notes: notes || undefined,
        },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: () => {
      toast.success(`Delegated to ${selectedPerson.label}`);
      onCreated?.();
      setSearch(''); setSelectedPerson(null); setScope('approval_authority');
      setStartDate(''); setEndDate(''); setNotes('');
    },
    onError: (e) => toast.error(`Delegation failed: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delegation-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Delegate Authority</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded border border-slate-800 bg-slate-950 p-2 text-xs">
            <div className="text-slate-500">Delegating from:</div>
            <div className="text-slate-200">
              {appointment?.person_name || '—'} · {appointment?.position_name || '—'}
              {appointment?.unit_name && <span className="text-slate-500"> · {appointment.unit_name}</span>}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Delegatee</div>
            {selectedPerson ? (
              <div className="flex items-center justify-between rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2">
                <div>
                  <div className="text-sm text-emerald-200" data-testid="delegatee-selected-label">{selectedPerson.label}</div>
                  <div className="text-[10px] text-slate-500">{selectedPerson.subtitle}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedPerson(null)}>Change</Button>
              </div>
            ) : (
              <>
                <Input
                  data-testid="delegate-search"
                  placeholder="Search Person Registry by name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-slate-950 border-slate-700"
                />
                <div className="mt-1 max-h-40 overflow-y-auto space-y-1">
                  {(personSearch.data?.items || []).map((it) => (
                    <button
                      type="button"
                      key={it.id}
                      data-testid={`delegate-person-${it.id}`}
                      onClick={() => setSelectedPerson(it)}
                      className="w-full rounded border border-slate-800 bg-slate-950 px-3 py-2 text-left text-sm hover:border-emerald-500 hover:bg-emerald-500/5"
                    >
                      <div className="text-slate-200">{it.label}</div>
                      <div className="text-[10px] text-slate-500">{it.subtitle}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Scope</div>
            <select
              data-testid="delegate-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
            >
              <option value="approval_authority">Approval authority</option>
              <option value="signing_authority">Signing authority</option>
              <option value="pastoral_care">Pastoral care</option>
              <option value="financial_approval">Financial approval</option>
              <option value="full_delegation">Full delegation</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Start date</div>
              <Input
                data-testid="delegate-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-slate-950 border-slate-700"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">End date (required)</div>
              <Input
                data-testid="delegate-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-slate-950 border-slate-700"
              />
            </div>
          </div>

          <Textarea
            data-testid="delegate-notes"
            placeholder="Notes / reason (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="bg-slate-950 border-slate-700 min-h-[60px]"
          />

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              data-testid="submit-delegation"
              className="bg-violet-600 hover:bg-violet-500"
              onClick={() => create.mutate()}
              disabled={!selectedPerson || !endDate || create.isPending}
            >
              <GitBranch className="mr-1 h-4 w-4" />
              {create.isPending ? 'Delegating…' : 'Confirm Delegation'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Message All Position Holders Dialog ─────────────────────────
function MessageAllDialog({ open, onOpenChange, position, activeCount }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [channel, setChannel] = useState('auto');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const templatesQ = useQuery({
    queryKey: ['msg-templates'],
    queryFn: async () => (await axios.get(`${API}/api/admin/message-templates`, { headers: authH() })).data,
    enabled: open,
  });

  const applyTemplate = (tplId) => {
    setSelectedTemplateId(tplId);
    if (!tplId) return;
    const tpl = (templatesQ.data?.items || []).find((t) => t.id === tplId);
    if (tpl) {
      setSubject(tpl.subject || '');
      setBody(tpl.body || '');
      if (tpl.channel_hint) setChannel(tpl.channel_hint);
    }
  };

  const send = useMutation({
    mutationFn: async () => {
      const r = await axios.post(
        `${API}/api/admin/leadership/positions/${position.id}/message-all`,
        {
          subject: subject.trim(),
          body: body.trim(),
          channel,
          template_id: selectedTemplateId || undefined,
        },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: (d) => {
      toast.success(`Dispatched to ${d.dispatched_count} holder(s). Skipped ${d.skipped_count}.`);
      onOpenChange(false);
      setSubject(''); setBody(''); setChannel('auto'); setSelectedTemplateId('');
    },
    onError: (e) => toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="message-all-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-sky-300" />
            Message All {position?.name} Holders
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-xs text-sky-200">
            One click will emit a <code className="text-sky-300">Member_Message_Requested</code> event for
            <span className="mx-1 font-semibold text-white">{activeCount}</span>
            current holder{activeCount === 1 ? '' : 's'}. Every send is routed through the notification engine and audited.
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Start from a template (optional)</div>
            <select
              data-testid="message-all-template"
              value={selectedTemplateId}
              onChange={(e) => applyTemplate(e.target.value)}
              className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
            >
              <option value="">— None —</option>
              {(templatesQ.data?.items || []).map((t) => (
                <option key={t.id} value={t.id}>
                  [{t.category}] {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Subject</div>
            <Input
              data-testid="message-all-subject"
              placeholder="e.g. Provincial Overseers meeting — Friday 7pm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-slate-950 border-slate-700"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Body</div>
            <Textarea
              data-testid="message-all-body"
              placeholder="Message content — use {{first_name}} and {{church_name}} to personalise."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="bg-slate-950 border-slate-700 min-h-[120px]"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Channel</div>
            <select
              data-testid="message-all-channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
            >
              <option value="auto">Auto (prefer email, fall back to SMS)</option>
              <option value="email">Email only</option>
              <option value="sms">SMS only</option>
              <option value="whatsapp">WhatsApp only</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              data-testid="send-message-all"
              className="bg-sky-600 hover:bg-sky-500"
              onClick={() => send.mutate()}
              disabled={!subject.trim() || !body.trim() || send.isPending || activeCount === 0}
            >
              <Send className="mr-1 h-4 w-4" />
              {send.isPending ? 'Dispatching…' : `Send to ${activeCount} Holder${activeCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Succession Playbook Dialog ──────────────────────────────────
function PlaybookDialog({ open, onOpenChange, position }) {
  const playbookQ = useQuery({
    queryKey: ['ldr-playbook', position?.id],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/leadership/positions/${position.id}/playbook`,
      { headers: authH() },
    )).data,
    enabled: open && !!position?.id,
  });

  const doPrint = () => {
    // The printable region has id `playbook-print-area` — spawn a fresh
    // window with just that HTML + a minimal print stylesheet so the
    // browser print dialog can generate a clean one-pager.
    const node = document.getElementById('playbook-print-area');
    if (!node) return;
    const win = window.open('', '_blank', 'width=900,height=1200');
    if (!win) return;
    win.document.write(`
      <html>
        <head>
          <title>${position?.name || 'Succession Playbook'}</title>
          <style>
            body { font-family: ui-sans-serif, system-ui, sans-serif; color: #0f172a; padding: 32px; }
            h1 { font-size: 20pt; margin: 0 0 4pt 0; }
            h2 { font-size: 12pt; margin: 16pt 0 6pt 0; color: #6b7280; text-transform: uppercase; letter-spacing: 1px; }
            table { width: 100%; border-collapse: collapse; margin-top: 6pt; font-size: 10pt; }
            th, td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; text-align: left; }
            th { background: #f3f4f6; font-weight: 600; }
            .muted { color: #6b7280; font-size: 9pt; }
            .badge { display: inline-block; padding: 1px 6px; border-radius: 6px;
                     background: #f3f4f6; font-size: 9pt; margin-left: 4pt; }
            .meta { font-size: 9pt; color: #6b7280; }
          </style>
        </head>
        <body>${node.innerHTML}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 350);
  };

  const d = playbookQ.data;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="playbook-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-300" />
            Succession Playbook · {position?.name}
          </DialogTitle>
        </DialogHeader>
        {playbookQ.isLoading && <p className="p-3 text-xs text-slate-500">Loading playbook…</p>}
        {d && (
          <div id="playbook-print-area" className="space-y-4 text-slate-100">
            <div>
              <h1 className="text-lg font-bold text-slate-100">{d.position?.name}</h1>
              <div className="meta muted text-[11px] text-slate-500">
                Level: {(d.position?.level || '').replace('_', ' ')}
                {' · '}Category: {d.position?.category || '—'}
                {' · '}Generated {new Date(d.generated_at).toLocaleString()}
              </div>
            </div>

            <div>
              <h2 className="text-[11px] uppercase tracking-wider text-slate-400">Current Holder{d.current_holders?.length === 1 ? '' : 's'} ({d.current_holders?.length || 0})</h2>
              {(d.current_holders || []).length === 0 ? (
                <p className="text-xs text-rose-300">Vacant — no active or acting appointment.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-slate-400">
                    <tr>
                      <th className="pb-1 text-left">Name</th>
                      <th className="pb-1 text-left">Status</th>
                      <th className="pb-1 text-left">Unit</th>
                      <th className="pb-1 text-left">Since</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.current_holders.map((a) => (
                      <tr key={a.id} className="border-t border-slate-800">
                        <td className="py-1">{a.person_name || '—'}<span className="muted"> · {a.person_email || ''}</span></td>
                        <td className="py-1">{a.status}{a.is_acting && <span className="badge">acting</span>}</td>
                        <td className="py-1">{a.unit_name || '—'}</td>
                        <td className="py-1">{a.start_date ? new Date(a.start_date).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div>
              <h2 className="text-[11px] uppercase tracking-wider text-slate-400">
                Last {Math.min(3, d.last_three_holders?.length || 0)} Holders <span className="muted">(of {d.total_history_count} total)</span>
              </h2>
              {(d.last_three_holders || []).length === 0 ? (
                <p className="text-xs text-slate-500">No historical appointments on record.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-slate-400">
                    <tr>
                      <th className="pb-1 text-left">Name</th>
                      <th className="pb-1 text-left">Status</th>
                      <th className="pb-1 text-left">Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.last_three_holders.map((a) => (
                      <tr key={a.id} className="border-t border-slate-800">
                        <td className="py-1">{a.person_name || '—'}</td>
                        <td className="py-1">{a.status}</td>
                        <td className="py-1">
                          {a.start_date ? new Date(a.start_date).toLocaleDateString() : '—'}
                          {' → '}
                          {a.end_date ? new Date(a.end_date).toLocaleDateString() : 'present'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div>
              <h2 className="text-[11px] uppercase tracking-wider text-slate-400">Active Delegations ({d.active_delegations?.length || 0})</h2>
              {(d.active_delegations || []).length === 0 ? (
                <p className="text-xs text-slate-500">No delegations currently active or scheduled.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-slate-400">
                    <tr>
                      <th className="pb-1 text-left">Delegator → Delegatee</th>
                      <th className="pb-1 text-left">Scope</th>
                      <th className="pb-1 text-left">Until</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.active_delegations.map((dl) => (
                      <tr key={dl.id} className="border-t border-slate-800">
                        <td className="py-1">{dl.delegator_name || '—'} → <span className="text-emerald-300">{dl.delegatee_name || '—'}</span></td>
                        <td className="py-1">{dl.scope}</td>
                        <td className="py-1">{dl.end_date ? new Date(dl.end_date).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            data-testid="playbook-print"
            className="bg-amber-600 hover:bg-amber-500"
            onClick={doPrint}
            disabled={!d}
          >
            <Printer className="mr-1 h-4 w-4" /> Print / Save PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

